/**
 * @fileoverview Shared constants and utilities for import template slideouts.
 * @module import-editor/components/ImportTemplateSlideoutShared
 *
 * Responsibilities:
 * - Provide shared constants for data mode, behavior, and visibility labels/colors
 * - Provide shared helper functions for date formatting and field mapping rendering
 * - Reduce duplication between system-web and tenant-web template slideout components
 *
 * Dependencies:
 * - @firesquirrel/local-db for type definitions
 *
 * Architecture:
 * - Contains pure helper functions/constants AND one shared React component (FieldMappingsTable).
 * - Used by both SystemImportTemplateSlideout and ImportTemplateSlideout
 *
 * Notes:
 * - System templates have visibility controls; tenant templates do not
 * - Tenant templates have run history; system templates do not
 */

import type {
  ImportTemplateVisibility,
  ImportDataMode,
  ImportBehavior,
  FieldMapping,
} from '@firesquirrel/local-db';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const VISIBILITY_LABELS: Record<ImportTemplateVisibility, string> = {
  'system-only': 'System Only',
  'all-departments': 'All Departments',
  'specific-tenants': 'Specific Tenants',
};

export const VISIBILITY_COLORS: Record<ImportTemplateVisibility, string> = {
  'system-only': 'bg-gray-100 text-gray-700 border border-gray-200',
  'all-departments': 'bg-green-50 text-green-700 border border-green-200',
  'specific-tenants': 'bg-blue-50 text-blue-700 border border-blue-200',
};

export const DATA_MODE_COLORS: Record<ImportDataMode, string> = {
  parcel: 'bg-blue-50 text-blue-700 border border-blue-200',
  lookup: 'bg-purple-50 text-purple-700 border border-purple-200',
  contact: 'bg-green-50 text-green-700 border border-green-200',
  generic: 'bg-gray-100 text-gray-700 border border-gray-200',
  parcelOwnerUpdate: 'bg-amber-50 text-amber-700 border border-amber-200',
};

export const BEHAVIOR_LABELS: Record<ImportBehavior, string> = {
  upsert: 'Upsert',
  insert: 'Insert Only',
  append: 'Append',
  'full-sync': 'Full Sync',
};

export const VISIBILITIES: ImportTemplateVisibility[] = [
  'system-only',
  'all-departments',
  'specific-tenants',
];

// ---------------------------------------------------------------------------
// Helper Functions
// ---------------------------------------------------------------------------

/**
 * Format an ISO date string to a readable date.
 */
export function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format an ISO date string to a readable date and time.
 */
export function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Field Mappings Table Component
// ---------------------------------------------------------------------------

interface FieldMappingsTableProps {
  mappings: FieldMapping[];
}

/**
 * Render a field mappings table with target field, source columns, transform, and match key.
 * This is a pure component that can be shared between system-web and tenant-web.
 */
export function FieldMappingsTable({ mappings }: FieldMappingsTableProps) {
  if (mappings.length === 0) {
    return (
      <div className="px-3 py-3 text-xs text-gray-400 italic">
        No field mappings defined.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs text-left">
        <thead className="bg-gray-50 border-t border-gray-200">
          <tr>
            <th className="px-3 py-2 font-semibold text-gray-600">
              Target Field
            </th>
            <th className="px-3 py-2 font-semibold text-gray-600">
              Source Column(s)
            </th>
            <th className="px-3 py-2 font-semibold text-gray-600">
              Transform
            </th>
            <th className="px-3 py-2 font-semibold text-gray-600 text-center w-20">
              Match Key
            </th>
          </tr>
        </thead>
        <tbody>
          {mappings.map((m, i) => (
            <tr
              key={i}
              className={`border-t border-gray-100 ${
                i % 2 === 1 ? 'bg-gray-50/50' : ''
              }`}
            >
              <td className="px-3 py-1.5 font-mono text-gray-800 text-[11px]">
                {m.targetField}
              </td>
              <td className="px-3 py-1.5 text-gray-600">
                {m.sourceColumns.join(', ') || '—'}
              </td>
              <td className="px-3 py-1.5 text-gray-500">
                {m.transform?.type ?? 'none'}
              </td>
              <td className="px-3 py-1.5 text-center">
                {m.isMatchKey ? (
                  <span className="text-orange-600 font-bold">✓</span>
                ) : (
                  ''
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}